import type { ActionItem, CustomerOverview, MonitorKarte, Money, TileKey, TileRow, Unternehmen }
  from "@kundeninfo/api-client";
import type { Objektart } from "@kundeninfo/ui/me-design";
import { formatBetrag, formatDatum, listText, monthName } from "../../format.js";
import { tageZwischen } from "../tickets/ticketFormat.js";
import { PAYMENT_TEXT } from "../suppliers/paymentState.js";

/*
 * Alle Anwendertexte der Kundenübersicht (Hausregel „Texte in Properties“, hier eine Tabelle je Sprache): Schlüssel
 * englisch, Werte deutsch, Wortlaut aus der Spec. Kein Baustein der Übersicht schreibt einen Text selbst.
 */

export const COV_TEXT = {
  actionTitle: "Handlungsbedarf",
  loading: "Übersicht wird geladen …",
  actionFailed: "Handlungsbedarf konnte nicht geladen werden",
  actionEmpty: "Kein Handlungsbedarf",
  actionIncomplete: "Handlungsbedarf unvollständig",
  notChecked: "nicht geprüft",
  retry: "Erneut laden",
  showLess: "Weniger zeigen",
  tilesFailed: "Nicht alle Kacheln konnten geladen werden.",
  tileFailed: "Konnte nicht geladen werden.",
  saveFailed: "Anordnung konnte nicht gespeichert werden – sie gilt bis zum Neuladen.",
  resetFailed: "Anordnung konnte nicht zurückgesetzt werden – bitte noch einmal versuchen.",
  customize: "Kacheln anpassen",
  done: "Fertig",
  allHidden: "Alle Kacheln ausgeblendet",
  chipsLabel: "Belegkacheln nach Unternehmen",
  /** Welche Kacheln die Unternehmenswahl betrifft — gebildet aus den Belegkacheln, die diese Akte zeigt (ohne
   *  Lieferantensicht ohne die Lieferantenkacheln, Final-Fixrunde Teil C). */
  chipsNote: (tiles: readonly string[]): string => `gilt für ${tiles.join(", ")}`,
  reloaded: "Übersicht aktualisiert",
  headerTitle: "Stammdaten und Kontakt",
  noAddress: "Keine Anschrift hinterlegt",
  addressesLink: "Adressen",
  noContact: "Kein Hauptansprechpartner",
  contactsLink: "Ansprechpartner",
  contactLoading: "Ansprechpartner wird geladen …",
  contactFailed: "Ansprechpartner nicht geladen",
  // Die Namen der Gruppen der Kopfkachel. Sie unterscheiden sich bewusst von denen des Akte-Kopfs („Anschrift“, „Kundennummern“):
  // beide stehen auf derselben Seite, und ein Name soll nur eine Gruppe benennen.
  numbersLabel: "Kundennummern und Status",
  addressLabel: "Hauptanschrift",
  contactLabel: "Hauptansprechpartner",
  central: "Zentrale",
  industryUnknown: "Branche unbekannt",
  lastActivity: "Letzte Aktivität",
  unknownCompany: "unbekanntes Unternehmen",
} as const;

/** Die Quellen des Handlungsbedarfs; die Folge der Schlüssel ist die Folge in „nicht geprüft: …“. */
export const MISSING_SOURCE_TEXT = { INVOICES: "Rechnungen", TICKETS: "Tickets", OFFERS: "Angebote",
  CHARGES: "Abrechnung", ORDERS: "Aufträge",
  // Lieferanten Strang E (Aufgabe 21)
  SUPPLIER_CONTRACTS: "Lieferverträge", INCOMING_INVOICES: "Eingangsrechnungen" } as const;

// --- Kacheln -----------------------------------------------------------------------------------------------------

type TileText = {
  title: string;
  /** Der Leertext; `hiddenMembers` zählt nur beim Konzern (nicht einsehbare Mitglieder). */
  empty: (hiddenMembers: number) => string;
  /** Der Text des „weitere“-Knopfs; `hiddenMembers` zählt nur beim Konzern (nicht einsehbare Mitglieder). */
  more: (total: number, hiddenMembers: number) => string;
};
const allOf = (what: string) => (total: number): string => `Alle ${total} ${what}`;
const always = (text: string) => (): string => text;

/** Titel, Leertext und „weitere“-Text je Kachel (Spec „Kacheln“). */
export const TILE_TEXT: Record<TileKey, TileText> = {
  importantNotes: { title: "Wichtiges", empty: always("Nichts Wichtiges hinterlegt."), more: allOf("Einträge") },
  tickets: { title: "Tickets", empty: always("Keine offenen Tickets."), more: allOf("Tickets") },
  invoices: { title: "Rechnungen", empty: always("Keine offenen Rechnungen."), more: allOf("offenen Rechnungen") },
  billing: { title: "Abrechnung", empty: always("Keine Fälligkeiten bis Ende nächsten Monats."), more: allOf("Fälligkeiten") },
  offers: { title: "Angebote", empty: always("Keine offenen Angebote."), more: allOf("offenen Angebote") },
  orders: { title: "Aufträge", empty: always("Keine laufenden Aufträge."), more: allOf("laufenden Aufträge") },
  notes: { title: "Notizen", empty: always("Keine Notizen."), more: allOf("Notizen") },
  files: { title: "Dateien", empty: always("Keine Dateien."), more: allOf("Dateien") },
  group: { title: "Konzern",
    empty: (hiddenMembers) => (hiddenMembers > 0 ? "Konzernmitglieder nicht einsehbar." : "Gehört zu keinem Konzern."),
    more: (_total, hiddenMembers) => (hiddenMembers > 0 ? `Konzern öffnen · ${hiddenMembers} nicht einsehbar` : "Konzern öffnen") },
  monitor: { title: "Monitor", empty: always("Kein Portal-Mandant zugeordnet."), more: allOf("Mandanten") },
  // Lieferanten Strang E (Aufgabe 21)
  supplierContracts: { title: "Lieferverträge", empty: always("Keine laufenden Verträge."), more: allOf("laufenden Verträge") },
  incomingInvoices: { title: "Eingangsrechnungen", empty: always("Keine offenen Eingangsrechnungen."),
    more: allOf("offenen Eingangsrechnungen") },
};

/** Die Kennzeichen einer Zeile (`TileRow.marker`); die Archivmarker liest auch der Reiter Konzern von hier. */
export const MARKER_TEXT: Record<NonNullable<TileRow["marker"]>, string> = {
  DRAFT_INVOICE: "Rechnung im Entwurf", WITHOUT_PLAN: "ohne Zahlplan", OFFER_DRAFT: "Entwurf", OFFER_SENT: "Gesendet",
  PARENT: "übergeordnet", PARENT_ARCHIVED: "Eltern archiviert", DAUGHTER: "Tochter", DAUGHTER_ARCHIVED: "Archiviert",
  ORDER_ENDING: "endet bald", HISTORICAL: "historisch",
};

/** Die Art eines Vorgangs als Wort (`TileRow.status` der Kachel „Dateien“: der erste verknüpfte Vorgang; im Reiter
 *  „Dateien“ auch Liefervertrag und Eingangsrechnung, nur mit Lieferantensicht, Aufgabe 9b). */
export const DOCUMENT_KIND_TEXT = {
  OFFER: "Angebot", SALES_ORDER: "Auftrag", INVOICE: "Rechnung",
  SUPPLIER_CONTRACT: "Liefervertrag", INCOMING_INVOICE: "Eingangsrechnung",
} as const;

/** Beschriftungen der Kennzahlen im Kachelkopf; `none` steht für „kein Wert“. Die Zustandswörter nehmen dieselben Wörter. */
export const FACT_TEXT = { overdue: "überfällig", open: "offen", missed: "verpasst", notBilled: "noch nicht abgerechnet",
  nextDeadline: "nächste Frist", withoutPlan: MARKER_TEXT.WITHOUT_PLAN, nextResubmission: "nächste Wiedervorlage",
  none: "–" } as const;

/** Wörter und Bausteine der Kachelzeilen. */
export const ROW_TEXT = {
  noDueDate: "ohne Fälligkeit",
  unlimited: "unbefristet",
  noOpenCharge: "keine Fälligkeit offen",
  unassigned: "nicht zugewiesen",
  unknown: "unbekannt",
  critical: "kritisch",
  important: "wichtig",
  invoiceFrom: (date: string): string => `Rechnung vom ${date}`,
  orderRef: (number: string): string => `Auftrag ${number}`,
  until: (date: string): string => `bis ${date}`,
  orderEnds: (title: string): string => `Auftrag ${title} endet`,
  openCharges: (n: number): string => countText(n, "Fälligkeit offen", "Fälligkeiten offen"),
  // Historische Vorgänge (Spec F4, 471): der Zusatz zum Kennzeichen „historisch“.
  historicalOffer: "Altbestand",
  historicalOrder: "ohne Abrechnung im System",
  // Die Domäne liefert am Nachfolger die nackte Nummer des ersetzten Angebots (477).
  replaces: (number: string): string => `ersetzt ${number}`,
  // Ein Rechnungsentwurf hat noch keine Nummer.
  draft: "Entwurf",
  firstLink: (kind: keyof typeof DOCUMENT_KIND_TEXT, number: string): string =>
    `${DOCUMENT_KIND_TEXT[kind]} ${number === "" ? ROW_TEXT.draft : number}`,
} as const;

/** Die Monitor-Kachel; `lage` erklärt kurz, warum zu einem Mandanten keine Daten da sind. */
export const MONITOR_TEXT = {
  rowName: (externalId: string): string => `Mandant ${externalId}`,
  openFindings: (n: number): string => countText(n, "offener Befund", "offene Befunde"),
  lage: { NICHT_ANGEBUNDEN: "Monitor nicht angebunden", NICHT_IM_MONITOR: "Im Monitor nicht angezeigt",
    KONFIGURATIONSFEHLER: "Zugang abgewiesen (Konfiguration)", NICHT_ERREICHBAR: "Monitor nicht erreichbar",
  } satisfies Record<Exclude<MonitorKarte["lage"], "ANGEZEIGT">, string>,
  cardFailed: "Monitor-Karte konnte nicht geladen werden",
  loadFailed: "Portal-Mandanten konnten nicht geladen werden",
  notConnected: "Monitor nicht angebunden",
  loading: "wird geladen …",
} as const;

// --- Lieferanten Strang E (Aufgabe 21) -----------------------------------------------------------------------------

/** „Oktober 2026“ für einen ISO-Tag: der Monat eines Fälligkeitenpostens („Zahlung überfällig: ‹Vertrag› ‹Monat›“). */
const monthText = (day: string): string => `${monthName(Number(day.slice(5, 7)))} ${day.slice(0, 4)}`;

/** „fällig heute“ / „fällig in n Tagen“ für einen Tag ab heute; ein vergangener Tag zählt als heute — dieselben Wörter
 *  wie das Zahlstatus-Kennzeichen (`PAYMENT_TEXT`). */
const payableText = (date: string, today: string): string => {
  const days = tageZwischen(today, date);
  return days <= 0 ? PAYMENT_TEXT.dueToday : PAYMENT_TEXT.dueIn(days);
};
/** „endet heute“ / „endet in n Tagen“ für den letzten Kündigungstag. */
const noticeEndsText = (date: string, today: string): string => {
  const days = tageZwischen(today, date);
  return days <= 0 ? "endet heute" : `endet in ${countText(days, "Tag", "Tagen")}`;
};

/**
 * Die Wörter der Lieferantenkacheln und des Lieferanten-Handlungsbedarfs (Spec „Kundenübersicht für Lieferanten“,
 * „Handlungsbedarf und Benachrichtigungen“). Die Kündigungsschwelle (`supplier.noticeWarningDays`) ist einstellbar und
 * steht nicht in der Antwort: kein Wort nennt eine feste Tageszahl.
 */
export const SUPPLIER_TEXT = {
  // Kennzahlen der Kacheln
  monthlyCost: "Kosten je Monat",
  noticeSoon: "Kündigungsfrist bald",
  openGross: "offene Summe brutto",
  // Zeilen der Kacheln
  entryNumber: (number: string): string => `E ${number}`,
  noticeUntil: (date: string): string => `Kündigungsfrist endet am ${date}`,
  // Handlungsbedarf: Zeilenname, Meta-Stück und Zustandswort je Art
  paymentOverdue: (supplier: string): string => `Zahlung überfällig: ${supplier}`,
  paymentDue: (date: string, today: string, supplier: string): string => `Zahlung ${payableText(date, today)}: ${supplier}`,
  payableWord: payableText,
  chargeOverdue: (contract: string, day: string): string => `Zahlung überfällig: ${contract} ${monthText(day)}`,
  invoiceMissing: (contract: string): string => `Rechnung fehlt: ${contract}`,
  invoiceMissingWord: "Rechnung fehlt",
  periodsSince: (n: number, date: string): string => `${countText(n, "Zeitraum", "Zeiträume")} seit ${formatDatum(date)}`,
  settlementMissing: (contract: string): string => `Jahresabrechnung fehlt: ${contract}`,
  settlementMissingWord: "Jahresabrechnung fehlt",
  settlementDay: (date: string): string => `Stichtag ${formatDatum(date)}`,
  noticeEnds: (date: string, contract: string): string => `Kündigungsfrist endet am ${formatDatum(date)}: ${contract}`,
  noticeEndsWord: noticeEndsText,
  withoutAccount: (supplier: string): string => `Eingangsrechnung ohne Konto: ${supplier}`,
  withoutAccountWord: "ohne Konto",
} as const;

// --- Tage, Beträge, Zahlwörter ------------------------------------------------------------------------------------

/** „3 Tage“ / „1 Tag“: Einzahl nur bei genau eins. */
export const countText = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** „seit n Tagen“ für einen vergangenen Tag; `today` ist der Tag vom Server, nie die Uhr des Geräts. */
export const sinceText = (date: string, today: string): string =>
  `seit ${countText(tageZwischen(date, today), "Tag", "Tagen")}`;

/** „läuft heute ab“ / „läuft in n Tagen ab“ für einen Tag ab heute. */
export function dueInText(date: string, today: string): string {
  const days = tageZwischen(today, date);
  return days === 0 ? "läuft heute ab" : `läuft in ${countText(days, "Tag", "Tagen")} ab`;
}

/** „03.11.“ im Jahr von `today`, sonst mit Jahr. */
export const shortDate = (day: string, today: string): string =>
  (day.slice(0, 4) === today.slice(0, 4) ? `${day.slice(8, 10)}.${day.slice(5, 7)}.` : formatDatum(day));

/** Beträge je Währung, mit „ · “ getrennt; ohne Beträge leer. */
export const moneyText = (amounts: readonly Money[]): string =>
  amounts.map((a) => formatBetrag(a.amount, a.currency)).join(" · ");

// --- Handlungsbedarf ----------------------------------------------------------------------------------------------

export const urgentText = (n: number): string => `${n} dringend`;
export const soonText = (n: number): string => `${n} bald`;

/** Der zugängliche Name der Überschrift: die Zahlen gehören hinein, nicht nur in die Kennzeichen. */
export const actionHeading = (counts: { error: number; warn: number }): string =>
  [COV_TEXT.actionTitle, counts.error > 0 ? urgentText(counts.error) : "", counts.warn > 0 ? soonText(counts.warn) : ""]
    .filter((part) => part !== "").join(", ");

export const showAllText = (total: number): string => `Alle ${total} zeigen`;
export const truncatedText = (shown: number, total: number): string =>
  `${shown} von ${total} gezeigt – die übrigen in den Reitern`;

/**
 * „Handlungsbedarf unvollständig – nicht geprüft: Rechnungen, Tickets“ — in der Folge von `MISSING_SOURCE_TEXT`, der
 * Ansprechpartner zählt nicht dazu. Ohne genannte Quelle (ein widersprüchlicher Stand) nur „Handlungsbedarf unvollständig“.
 */
export function incompleteText(missing: readonly CustomerOverview["missingSources"][number][]): string {
  const names = (Object.keys(MISSING_SOURCE_TEXT) as (keyof typeof MISSING_SOURCE_TEXT)[])
    .filter((source) => missing.includes(source)).map((source) => MISSING_SOURCE_TEXT[source]);
  return names.length === 0 ? COV_TEXT.actionIncomplete : `${COV_TEXT.actionIncomplete} – ${COV_TEXT.notChecked}: ${names.join(", ")}`;
}

/** Die Ansage nach einem Sichtwechsel, mit Kürzeln; nur mit mindestens einem Kürzel aufrufen (die Sicht ist nie leer). */
export const viewAnnouncement = (codes: readonly string[]): string => `Belegkacheln zeigen jetzt ${listText(codes)}`;

// --- Zustand als Wort ---------------------------------------------------------------------------------------------

/** Wovon die Rede ist: wählt das Zustandswort („überfällig“, „verpasst“, „abgelaufen“ …). */
export type LevelArt = "due" | "charge" | "deadline" | "end" | "resubmission";
export type LevelWord = { statusArt?: "fehler" | "warnung"; status?: string };

/** Die Kennzeichen-Art einer Stufe — EINE Abbildung für Zeilen, Handlungsbedarf und Belegkacheln (Final-Fixrunde Teil C). */
export const levelStatusArt = (level: "ERROR" | "WARN"): "fehler" | "warnung" => (level === "ERROR" ? "fehler" : "warnung");
const ERROR_WORD: Record<LevelArt, string> = { due: FACT_TEXT.overdue, charge: FACT_TEXT.missed, deadline: "abgelaufen",
  end: "beendet", resubmission: FACT_TEXT.overdue };
const WARN_WORD: Record<LevelArt, string> = { due: "heute fällig", charge: "heute fällig", deadline: "läuft bald ab",
  end: "endet heute", resubmission: "heute" };

/**
 * Der Zustand einer Zeile als Wort neben der Farbe (nie nur über Farbe): ERROR „… seit n Tagen“, WARN je Art.
 * Ohne Datum steht nur das Wort („abgelaufen“, „läuft bald ab“); ohne Stufe nichts. WARN heißt außer bei Fristen
 * „heute“: Die Domäne stuft Rechnungen, Posten, Wiedervorlagen und Auftragsende ohne Vorwarnzeit.
 */
export function levelWord(level: "ERROR" | "WARN" | null, date: string | null, today: string, art: LevelArt): LevelWord {
  if (level === null) return {};
  const status = level === "ERROR"
    ? (date === null ? ERROR_WORD[art] : `${ERROR_WORD[art]} ${sinceText(date, today)}`)
    : (art === "deadline" && date !== null ? dueInText(date, today) : WARN_WORD[art]);
  return { statusArt: levelStatusArt(level), status };
}

// --- Unternehmen und Zeilen des Handlungsbedarfs ------------------------------------------------------------------

/** Kürzel, Name und Hausfarbe eines Unternehmens über seine Kennung; ein unbekanntes zeigt „?“. */
export function companyOf(id: string, companies: readonly Pick<Unternehmen, "id" | "name" | "shortCode" | "colorHex">[]):
    { code: string; name: string; color: string | undefined } {
  const company = companies.find((c) => c.id === id);
  return company === undefined ? { code: "?", name: COV_TEXT.unknownCompany, color: undefined }
    : { code: company.shortCode, name: company.name, color: company.colorHex };
}

export type ActionRowView = {
  art: Objektart; name: string; kennung: string; meta: string; statusArt?: "fehler" | "warnung"; status?: string;
  companyCode: string; companyName: string; companyColor: string | undefined; ariaLabel: string;
};

type KindView = {
  /** Das Symbol; je Zeile, wo eine Art an Rechnung und Vertrag vorkommt („Zahlung fällig“, Dirk 514). */
  art: Objektart | ((item: ActionItem) => Objektart);
  name: (item: ActionItem, today: string) => string;
  /** Das erste Stück der Meta-Zeile. */
  lead: (item: ActionItem, today: string) => string;
  /** Statt `lead` im zugänglichen Namen, wo `lead` „seit n Tagen“ sagt: das Datum ausgeschrieben (der Zustand sagt die Tage). */
  dated?: (item: ActionItem, today: string) => string;
  state: (item: ActionItem, today: string) => LevelWord;
  /** Der Name sagt den Zustand schon („Wiedervorlage heute“, „Angebot läuft in 5 Tagen ab“): im zugänglichen Namen nicht zweimal. */
  stateInName?: true;
};
const byLevel = (art: LevelArt): KindView["state"] => (item, today) => levelWord(item.level, item.date, today, art);
const fixedWord = (status: string): KindView["state"] => (item) => ({ statusArt: levelStatusArt(item.level), status });
const since: KindView["lead"] = (item, today) => sinceText(item.date, today);
const chargesSince: KindView["lead"] = (item) =>
  `${countText(item.count, "Fälligkeit", "Fälligkeiten")} seit ${formatDatum(item.date)}`;
const dueOn: KindView["lead"] = (item) => `fällig am ${formatDatum(item.date)}`;
const validUntil: KindView["lead"] = (item) => `gültig bis ${formatDatum(item.date)}`;

const ACTION_KIND: Record<ActionItem["kind"], KindView> = {
  INVOICE_OVERDUE: { art: "rechnung", name: () => "Rechnung überfällig", lead: since, dated: dueOn,
    state: byLevel("due") },
  RESUBMIT_OVERDUE: { art: "ticket", name: (item) => `Wiedervorlage überfällig: ${item.title}`, lead: since, dated: dueOn,
    state: byLevel("resubmission") },
  // Die Uhrzeit gehört nur zur Wiedervorlage von heute.
  RESUBMIT_TODAY: { art: "ticket", name: (item) => `Wiedervorlage heute: ${item.title}`,
    lead: (item) => (item.time === null ? "heute" : `heute ${item.time} Uhr`),
    dated: (item) => formatDatum(item.date) + (item.time === null ? "" : ` ${item.time} Uhr`), state: byLevel("resubmission"),
    stateInName: true },
  OFFER_EXPIRED: { art: "angebot", name: (item) => `Angebot abgelaufen ohne Antwort: ${item.title}`, lead: since,
    dated: validUntil, state: byLevel("deadline") },
  OFFER_EXPIRING: { art: "angebot", name: (item, today) => `Angebot ${dueInText(item.date, today)}: ${item.title}`,
    lead: validUntil, state: byLevel("deadline"), stateInName: true },
  CHARGE_MISSED: { art: "abrechnung", name: (item) => `Fälligkeit verpasst, keine Rechnung: ${item.title}`,
    lead: chargesSince, state: byLevel("charge") },
  CHARGE_MISSED_DRAFT: { art: "abrechnung", name: (item) => `Fälligkeit verpasst, Rechnung nur im Entwurf: ${item.title}`,
    lead: chargesSince, state: fixedWord("Rechnung nur im Entwurf"), stateInName: true },
  ORDER_WITHOUT_PLAN: { art: "auftrag", name: (item) => `Laufender Auftrag ohne Zahlplan: ${item.title}`,
    lead: (item) => `seit ${formatDatum(item.date)}`, state: fixedWord(MARKER_TEXT.WITHOUT_PLAN), stateInName: true },
  // Lieferanten Strang E (Aufgabe 21). `title` ist der Lieferant bzw. der Vertragstitel; Verträge haben keine Nummer (`reference`
  // ""), `date` eines Postens ist der Beginn des Zeitraums (nicht die Fälligkeit: kein „seit n Tagen“).
  SUPPLIER_PAYMENT_OVERDUE: { art: "rechnung", name: (item) => SUPPLIER_TEXT.paymentOverdue(item.title), lead: since, dated: dueOn,
    state: byLevel("due") },
  // Dirk 514: auch ein Posten ohne Rechnung per Überweisung (Sprung in die Lieferverträge); `date` ist dort die Fälligkeit.
  SUPPLIER_PAYMENT_DUE: { art: (item) => (item.target.kind === "TAB" && item.target.tab === "liefervertraege" ? "vertrag" : "rechnung"),
    name: (item, today) => SUPPLIER_TEXT.paymentDue(item.date, today, item.title),
    lead: dueOn, state: (item, today) => ({ statusArt: levelStatusArt(item.level), status: SUPPLIER_TEXT.payableWord(item.date, today) }),
    stateInName: true },
  SUPPLIER_CHARGE_OVERDUE: { art: "vertrag", name: (item) => SUPPLIER_TEXT.chargeOverdue(item.title, item.date), lead: () => "",
    state: fixedWord(FACT_TEXT.overdue), stateInName: true },
  SUPPLIER_INVOICE_MISSING: { art: "vertrag", name: (item) => SUPPLIER_TEXT.invoiceMissing(item.title),
    lead: (item) => SUPPLIER_TEXT.periodsSince(item.count, item.date), state: fixedWord(SUPPLIER_TEXT.invoiceMissingWord),
    stateInName: true },
  SUPPLIER_SETTLEMENT_MISSING: { art: "vertrag", name: (item) => SUPPLIER_TEXT.settlementMissing(item.title),
    lead: (item) => SUPPLIER_TEXT.settlementDay(item.date), state: fixedWord(SUPPLIER_TEXT.settlementMissingWord),
    stateInName: true },
  SUPPLIER_NOTICE_DEADLINE: { art: "vertrag", name: (item) => SUPPLIER_TEXT.noticeEnds(item.date, item.title), lead: () => "",
    state: (item, today) => ({ statusArt: levelStatusArt(item.level), status: SUPPLIER_TEXT.noticeEndsWord(item.date, today) }) },
  SUPPLIER_WITHOUT_ACCOUNT: { art: "rechnung", name: (item) => SUPPLIER_TEXT.withoutAccount(item.title), lead: dueOn,
    state: fixedWord(SUPPLIER_TEXT.withoutAccountWord), stateInName: true },
};

const compact = (parts: readonly string[], separator: string): string => parts.filter((part) => part !== "").join(separator);

/**
 * Eine Zeile der Leiste Handlungsbedarf als Anzeigedaten. `ariaLabel` nennt Kennung, das Datum ausgeschrieben, das
 * Unternehmen mit Namen und den Zustand als Wort — „seit n Tagen“ / „in n Tagen“ nur einmal, und ein Zustand, den der
 * Name der Zeile schon sagt, nicht ein zweites Mal.
 */
export function actionRowView(item: ActionItem, today: string, companies: readonly Unternehmen[]): ActionRowView {
  const kind = ACTION_KIND[item.kind];
  const company = companyOf(item.companyId, companies);
  const name = kind.name(item, today);
  const rest = [moneyText(item.amounts), item.detail ?? ""];
  const state = kind.state(item, today);
  return { art: typeof kind.art === "function" ? kind.art(item) : kind.art, name, kennung: item.reference, meta: compact([kind.lead(item, today), ...rest], " · "), ...state,
    companyCode: company.code, companyName: company.name, companyColor: company.color,
    ariaLabel: compact([compact([name, item.reference], " "), compact([(kind.dated ?? kind.lead)(item, today), ...rest], " · "),
      company.name, kind.stateInName === true ? "" : state.status ?? ""], ", ") };
}
